import { useCallback, useState } from "react";

import { useRouter } from "next/router";

import { IGroup } from "pages/[tenant]/g/@types";
import useGroupContext from "pages/[tenant]/g/hooks/useGroupContext";
import { getGroupDataAvailablePublicOffer } from "pages/[tenant]/g/utils/fetchGroupSlug";
import { removeDuplicatedObjects } from "pages/[tenant]/g/utils/removeDuplicatedObjects";

import api from "shared/infra/services/api";
import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import { captureException, captureMessage } from "@sentry/nextjs";

import usePagePresentationContext from "./useProductPresentationContext";

const useProductPresentationService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { setTopics, group, setGroup } = useGroupContext();
  const { setAditionalGroupsNexusList } = usePagePresentationContext();

  const router = useRouter();

  const loadAllGroupTopics = useCallback(
    async ({ groupId }): Promise<void> => {
      try {
        setIsLoading(true);

        const response = await api.get(
          `group-lessons/${groupId}/list?perPage=999`,
        );
        const topics = response?.data?.data;

        setTopics((prev) => removeDuplicatedObjects([...prev, ...topics]));
      } catch (error) {
        console.log("Erro (loadAllTopicsFromGroup): ", error);
        openNotificationWithIcon(
          "info",
          "Ocorreu um erro aos buscar a lista de aulas do grupo.",
        );
        captureException(error);
        captureMessage("Erro (loadAllTopicsFromGroup)");
      } finally {
        setIsLoading(false);
      }
    },
    [setTopics],
  );

  const handleGroupAccess = useCallback(
    async ({ subscriptionMethod }) => {
      try {
        setIsLoading(true);

        if (subscriptionMethod !== "paid_access") {
          await api.post(`/groups/${group?.id}/subscribe`);
        }

        if (subscriptionMethod === "request_access") {
          setGroup((prev) => ({
            ...prev,
            member_info: { ...prev?.member_info, has_active_sub_request: true },
          }));
        }

        if (subscriptionMethod === "free_access")
          return router.push(`/g/${group?.slug}`);
      } catch (err) {
        const errorMessage = err?.response?.data?.message;

        const userAlreadyInGroup = errorMessage?.includes(
          "User already subscribed to the group",
        );
        if (userAlreadyInGroup) return;

        console.log("Error (handleRequestParticipation): ", err);
        openNotificationWithIcon(
          "info",
          "Ocorreu um erro ao solicitar sua participação no grupo. Por favor, tente novamente mais tarde.",
        );
        captureException(err);
        captureMessage("Error (handleRequestParticipation)");

        location.reload();
      } finally {
        setIsLoading(false);
      }
    },
    [group?.id, group?.slug, router, setGroup],
  );

  const getNexusByGroupId = useCallback(
    async ({ groupId }) => {
      try {
        const { data } = await api.get(`/nexus/bindings/${groupId}/targets`, {
          params: {
            page: 1,
            perPage: 20,
          },
        });

        setAditionalGroupsNexusList(data?.data);
      } catch (error) {
        console.log("Erro (getNexusByGroupId): ", error);
        captureException(error);
        openNotificationWithIcon(
          "info",
          "Ocorreu um erro ao buscar os conteúdos vinculados ao grupo.",
        );
        captureMessage("Erro (getNexusByGroupId)");
      }
    },
    [setAditionalGroupsNexusList],
  );

  const getGroupWithPublicOffers = useCallback(
    async ({ group_slug }) => {
      try {
        const groupWithOffers = await getGroupDataAvailablePublicOffer({
          group_slug,
          headers: {},
        });

        setGroup(groupWithOffers);
      } catch (error) {
        console.log("Erro (getNexusByGroupId): ", error);
        openNotificationWithIcon(
          "info",
          "Ocorreu um erro ao buscar as ofertas adicionais do grupo.",
        );
        captureException(error);
        captureMessage("Erro (getNexusByGroupId)");
      }
    },
    [setGroup],
  );

  const getCompleteInformationAboutGroup = useCallback(
    async ({ groupSlug, groupId }) => {
      try {
        setIsLoading(true);
        const response = await api.get(
          `groups/${groupSlug}?with_offers=1&available_offers=1&public_offers=1`,
        );
        const groupFromResponse: IGroup = response.data.data;
        setAditionalGroupsNexusList((prev) =>
          prev?.map((g) =>
            g?.id === groupId
              ? { ...groupFromResponse, is_completed: true }
              : g,
          ),
        );
      } catch (error) {
        captureException(error);
        captureMessage(error);
        console.log(
          `Error (useProductPresentationService::getGroup): ${error}`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [setAditionalGroupsNexusList],
  );

  return {
    loadAllGroupTopics,
    handleGroupAccess,
    getNexusByGroupId,
    getGroupWithPublicOffers,
    getCompleteInformationAboutGroup,
    isLoading,
  };
};

export default useProductPresentationService;
